/**
 * Los mensajes con los que se manda el enlace al cliente (correo y WhatsApp), redactados para quien los recibe:
 * saludo con su nombre, qué va a ver, hasta cuándo sirve y cómo pedir ayuda. Nada de jerga del sistema.
 * Funciones puras: se prueban sin pantalla.
 */

export interface DatosDelMensaje {
  /** Nombre de la obra. */
  obra: string;
  /** Para quién es (opcional): se usa en el saludo. */
  cliente?: string | null;
  url: string;
  /** ISO de cuándo deja de servir el enlace. */
  expiraAt: string;
  /** Si el enlace muestra precios (cambia lo que se promete que verá). */
  conPrecios: boolean;
  /** Quien manda el mensaje (firma). */
  remitente?: string | null;
}

export const fechaLarga = (iso: string): string =>
  new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });

const saludo = (cliente?: string | null): string => {
  const n = cliente?.trim();
  return n ? `Hola ${n}` : 'Hola';
};

const queVera = (conPrecios: boolean): string =>
  conPrecios ? 'el plano y cada concepto con su cantidad y su precio' : 'el plano y cada concepto con su cantidad';

export function asuntoDelCorreo(d: Pick<DatosDelMensaje, 'obra'>): string {
  return `Presupuesto de ${d.obra}`;
}

export function cuerpoDelCorreo(d: DatosDelMensaje): string {
  const firma = d.remitente?.trim() ? `\n\nSaludos,\n${d.remitente.trim()}` : '\n\nSaludos.';
  return (
    `${saludo(d.cliente)}:\n\n` +
    `Te comparto el presupuesto de ${d.obra}. Puedes verlo desde este enlace, sin crear cuenta ni instalar nada:\n\n` +
    `${d.url}\n\n` +
    `Ahí encontrarás ${queVera(d.conPrecios)}. En cada concepto, «Ver cómo se calculó» te muestra de dónde sale su cantidad.\n\n` +
    `El enlace estará disponible hasta el ${fechaLarga(d.expiraAt)}. Si tienes cualquier duda o quieres ajustar algo, con gusto lo vemos.` +
    firma
  );
}

export function textoDeWhatsApp(d: DatosDelMensaje): string {
  return (
    `${saludo(d.cliente)}, te comparto el presupuesto de ${d.obra}:\n${d.url}\n\n` +
    `Ahí ves ${queVera(d.conPrecios)}, sin crear cuenta. Está disponible hasta el ${fechaLarga(d.expiraAt)}. ` +
    `Cualquier duda, con gusto la vemos.`
  );
}

/** Solo los dígitos de un teléfono (con lada del país). null si no alcanzan para ser un número. */
export function telefonoParaWhatsApp(texto: string): string | null {
  const d = texto.replace(/\D/g, '');
  return d.length >= 8 && d.length <= 15 ? d : null;
}

export const correoValido = (t: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t.trim());

/** Abre WhatsApp con el chat de ese número (o sin destinatario) y el texto ya escrito. */
export function urlDeWhatsApp(texto: string, telefono: string | null): string {
  return `https://wa.me/${telefono ?? ''}?text=${encodeURIComponent(texto)}`;
}

/** Abre el programa de correo con destinatario, copia, asunto y cuerpo ya escritos. */
export function urlDeCorreo(p: { para?: string | null; copia?: string | null; asunto: string; cuerpo: string }): string {
  const q = [`subject=${encodeURIComponent(p.asunto)}`, `body=${encodeURIComponent(p.cuerpo)}`];
  if (p.copia?.trim()) q.push(`cc=${encodeURIComponent(p.copia.trim())}`);
  return `mailto:${p.para?.trim() ? encodeURIComponent(p.para.trim()) : ''}?${q.join('&')}`;
}
